/**
 * Role Constants
 * Single source of truth for all role types and role-based access rules.
 */

export type RoleType = string | null | undefined;

// All known Strapi role types
export const ROLE_TYPES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  OPERATOR: 'operator',
  KIOSK_USER: 'kiosk_user',
  SALES_OFFICE_USER: 'sales_office_user',
  FINANCE_ADMIN: 'finance_admin',
  SECURITY_GUARD: 'security_guard',
  SAFETY_MANAGER: 'safety_manager',
  SERVICES_MANAGER: 'services_manager',
  TENANT_ADMIN: 'tenant_admin',
  TENANT_USER: 'tenant_user',
  CUSTOMER_OPERATOR: 'customer_operator',
  EMPLOYEE: 'employee',
  CUSTOMER: 'customer',
  ECOMMERCE: 'ecommerce',
  AUTHENTICATED: 'authenticated',
  PUBLIC: 'public',
  SCALE_OPERATOR: 'scale_operator',
  SCALE_ADMIN: 'scale_admin',
  PACKHOUSE_MANAGER: 'packhouse_manager',
} as const;

// Gate portal: routes scale_operator / scale_admin are allowed to access
export const GATE_PORTAL_SCALE_ALLOWED_ROUTES = [
  '/weighbridge-ops',
  '/weighbridge-corrections',
] as const;

// Roles with admin-level portal access (elevated permissions)
export const ADMIN_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
] as const;

// Roles that can access the admin portal UI
export const ADMIN_PORTAL_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
] as const;

// Roles that bypass tenant requirement (no tenant needed to login)
export const TENANT_BYPASS_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SECURITY_GUARD,
  ROLE_TYPES.SAFETY_MANAGER,
  ROLE_TYPES.SERVICES_MANAGER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.EMPLOYEE,
  ROLE_TYPES.PACKHOUSE_MANAGER,
] as const;

// Roles that get admin init data on login
export const ADMIN_INIT_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.PACKHOUSE_MANAGER,
] as const;

// Privileged roles that can use tenant override header
export const PRIVILEGED_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
] as const;

// System user roles (users without tenant are system-level)
export const SYSTEM_USER_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SECURITY_GUARD,
  ROLE_TYPES.SAFETY_MANAGER,
  ROLE_TYPES.SERVICES_MANAGER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.EMPLOYEE,
  ROLE_TYPES.PACKHOUSE_MANAGER,
] as const;

// Gate portal: routes only accessible to super_admin
export const GATE_PORTAL_SUPER_ADMIN_ROUTES = [
  '/factories',
  '/accommodations',
] as const;

// Gate portal: roles not allowed to change factory context in header selector
export const GATE_FACTORY_SELECTOR_DENIED_ROLE_TYPES = [
  ROLE_TYPES.SECURITY_GUARD,
  ROLE_TYPES.SAFETY_MANAGER,
  ROLE_TYPES.SERVICES_MANAGER,
  ROLE_TYPES.EMPLOYEE,
  ROLE_TYPES.SCALE_OPERATOR,
  ROLE_TYPES.SCALE_ADMIN,
  ROLE_TYPES.PACKHOUSE_MANAGER,
] as const;

// Admin portal: routes the 'admin' role is NOT allowed to access
// (super_admin-only features)
export const ADMIN_ROLE_RESTRICTED_ROUTES = [
  '/system/sap-configuration',
  '/system/simulation',
  '/system/alarm-configuration',
  '/system/alarms',
  '/system/hardware-logs',
  '/system/inbound-simulator',
] as const;

// Admin portal: routes finance_admin is allowed to access
export const FINANCE_ADMIN_ALLOWED_ROUTE_PREFIXES = [
  '/orders/fulfillment',
  '/orders/sales-orders',
  '/orders/incoming-materials',
  '/catalog',
  '/finance',
  '/organization/customer-accounts',
  '/organization/customers',
] as const;

// Roles allowed to mutate visitors (create, update, delete, approve, check-in/out, cancel)
// security_guard can only VIEW visitors
export const VISITOR_MANAGE_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.EMPLOYEE,
] as const;

// Gate portal: routes the employee role is allowed to access
export const GATE_PORTAL_EMPLOYEE_ALLOWED_ROUTES = [
  '/visitors',
  '/settings',
] as const;

// Gate portal: routes the security_guard role is allowed to access
export const GATE_PORTAL_SECURITY_GUARD_ALLOWED_ROUTES = [
  '/visitors',
  '/access-logs',
  '/departments',
  '/employees',
] as const;

// Gate portal: routes the services_manager role is allowed to access
export const GATE_PORTAL_SERVICES_MANAGER_ALLOWED_ROUTES = [
  '/visitors',
  '/accommodations',
  '/settings',
] as const;

// Gate portal: routes the packhouse_manager role is allowed to access
export const GATE_PORTAL_PACKHOUSE_MANAGER_ALLOWED_ROUTES = [
  '/',
  '/loading-locations',
  '/orders',
] as const;
